import React, { HTMLAttributeAnchorTarget, memo, ReactNode } from 'react';

import type { RouterProps } from '../../internal/types/routerTypes';
import { TextRenderFunction } from '../Text/Text';
import { TextProps } from '../Text';
import { TextSize } from '../Text/types';
import { textSkinSizes } from '../Text/utils';
import { TextAlign, TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { HelpProps } from '../Help';

import { wrapWithLink } from './utils';

interface TextCellContentProps extends RouterProps {
    align?: TextAlign;
    href?: string;
    textHandler?: () => void;
    content: string | TextRenderFunction;
    size: TextSize;
    isTitle?: boolean;
    isTruncated?: boolean;
    help?: (helpProps: HelpProps) => ReactNode;
    target?: HTMLAttributeAnchorTarget;
    color?: TextProps['color'];
}

export const TextCellContent = memo<TextCellContentProps>(function TextCellContent({
    align,
    href,
    isTitle = true,
    isTruncated,
    content,
    size,
    target,
    routerOptions,
    textHandler,
    help,
    color,
}) {
    const hasHandler = Boolean(textHandler);
    const hasHref = Boolean(href);

    const linkColor = isTitle ? 'main' : 'supplementary';
    const role = isTitle ? 'main' : 'supplementary';

    if (typeof content === 'string') {
        return (
            <TextGenericWithSkin
                help={help}
                textAlign={align}
                title={isTruncated ? content : undefined}
                textSize="current-main"
                skinSize={textSkinSizes[size]}
                color={color ?? 'current'}
                role={role}
                isTruncated={isTruncated}
            >
                {hasHandler || hasHref
                    ? wrapWithLink({
                          text: content,
                          color: linkColor,
                          href,
                          onClick: textHandler,
                          target,
                          routerOptions,
                      })
                    : content}
            </TextGenericWithSkin>
        );
    }

    return (
        <>
            {content({
                role,
                size,
                textAlign: align,
                isTruncated,
                color,
            })}
        </>
    );
});
